/**
 * A page's SEO head: `<title>`, description, Open Graph and Twitter tags, canonical link,
 * viewport, a no-JavaScript notice, and optionally Ryerson's Organization JSON-LD.
 *
 * Position: rendered once per page, about 200 importers. Fixed pages pass the language's entry
 * of the `MetaObject` exported by their `src/content/**` module (for example
 * `src/pages/login/index.tsx` with `content/shared/login` → `LoginMetaObject`). Contentful-driven
 * pages build the `MetaContent` from the entry's SEO fields
 * (`components/Contentful/ContentfulPage.tsx`, `Product.tsx`, `SubProduct.tsx`). The same
 * `MetaContent` objects sit in `StaticPageContent.meta`, which `scripts/staticSearch.ts`
 * serialises into the site search index.
 *
 * Rules a change must keep:
 * - Every page has a title, a description and a canonical URL. The canonical is
 *   `MetaContent.canonicalUrl` when set, otherwise `NEXT_PUBLIC_ENV_URL` + `MetaContent.url`.
 * - A few pages are handled specially for SEO, per business requirements. Today that is the
 *   Contentful product sub-category pages: `components/Contentful/SubProduct.tsx` sets
 *   `canonicalUrl` to the entry's store shopping link when it has one. The product-category URLs
 *   in `marketing-custom-paths.js` are also kept out of the marketing sitemap
 *   (`next-sitemap.config.js` → `exclude`).
 * - The viewport's `maximum-scale=1` stops Safari on iPhone zooming in when an input is focused,
 *   for example the global search box.
 *
 * The JSON-LD values (name, `https://ryerson.com`, logo, social links, the US phone number) are
 * Ryerson's on every build.
 */
import React from "react";
import { PhoneNumbers } from "@ryerson/common.constants";
import Head from "next/head";

/**
 * One language's SEO fields for a page. Pages and content modules hold these as
 * `LanguageContent<MetaContent>`.
 */
export type MetaContent = {
	title: string;
	description: string;
	/**
	 * `og:image` / `twitter:image`. Absent: the site-relative hard-hat image in `public/images/`.
	 */
	imageUrl?: string;
	/**
	 * The page's path from the site root, for example `/login`; prefixed with
	 * `NEXT_PUBLIC_ENV_URL`.
	 */
	url: string;
	/**
	 * `true` adds the Organization JSON-LD block. Fixed pages set `true`; the Contentful, blog,
	 * podcast, news, location and leader pages set `false`.
	 */
	company: boolean;
	/** Full canonical URL, overriding `NEXT_PUBLIC_ENV_URL` + `url`. */
	canonicalUrl?: string;
};

/** Props of {@link MetaHead}. `children` are extra `<head>` elements, rendered last. */
export type MetaProps = {
	content: MetaContent;
	children?: any;
};

/**
 * Writes the page's SEO tags into `next/head`. With `NEXT_PUBLIC_ENV_URL` unset, `og:url` and the
 * default canonical are the bare path.
 */
const MetaHead: React.FC<MetaProps> = ({ content, children }) => {
	const title = content.title;
	const description = content.description;
	const imageUrl = content.imageUrl
		? content.imageUrl
		: "/images/why-ryerson/ryerson-commitment/WhiteRyersonHardhat.png";
	const url = (process.env.NEXT_PUBLIC_ENV_URL ?? "") + content.url;
	const company = content.company;

	const CompanyJson: any = {
		"@context": "http://schema.org",
		"@type": "Organization",
		url: "https://ryerson.com",
		name: "Ryerson",
		logo: `${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/logo/ryerson.svg`,
		sameAs: [
			"https://www.facebook.com/ryersonmetals/?fref=nf",
			"https://www.instagram.com/ryersonmetals/?hl=en",
			"https://www.linkedin.com/company/ryerson?trk=top_nav_home",
			"https://www.youtube.com/channel/UCAjLhGiGaSBbTOwq52rZNAw",
		],
		contactPoint: [
			{
				"@type": "ContactPoint",
				telephone: PhoneNumbers.RYERSON_US.number,
				contactType: "customer service",
			},
		],
	};

	return (
		<Head>
			<title>{title}</title>
			<meta name="title" content={title} />
			<meta property="og:title" content={title} />
			<meta name="twitter:title" content={title} />
			<meta name="description" content={description} />
			<meta name="twitter:description" content={description} />
			<meta property="og:description" content={description} />
			<meta property="og:image" content={imageUrl} />
			<meta property="og:url" content={url} />
			<meta name="twitter:image" content={imageUrl} />
			{/* twitter:card carries the image URL, not a card type such as
			"summary_large_image". */}
			<meta name="twitter:card" content={imageUrl} />
			{/* viewport meta tag was added to resolve automatic zoom in for mobile devices in Safari, reference story RCW-7269 */}
			{/* width=device-width: Sets the width of the page to match the device's screen width.
			initial-scale=1: Sets the initial zoom level to no zoom. 
			maximum-scale=1: Prevents automatic zooming beyond the initial scale.*/}
			<meta
				name="viewport"
				content="maximum-scale=1, width=device-width, initial-scale=1, viewport-fit=cover"
				key="viewport"
			/>
			{company === true && (
				<script type="application/ld+json">{JSON.stringify(CompanyJson)}</script>
			)}
			<link rel="canonical" href={content.canonicalUrl ?? url} />
			<noscript>
				{
					"This page requires JavaScript to function properly. Please enable JavaScript in your browser and reload the page."
				}
			</noscript>
			{children && children}
		</Head>
	);
};

export default MetaHead;
